import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, MessageSquare, TrendingUp, Calendar, Crown,
  Download, Search, ChevronRight, Shield, LogOut,
  BarChart3, Target, Sparkles, DollarSign, CreditCard, 
  Ticket, Copy, Plus, Trash2, CheckCircle, XCircle, Clock,
  Upload, Mail, Send, FileSpreadsheet, X, Loader2, RefreshCw, Gift, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';
import { BecomingHerEmails } from '@/components/admin/BecomingHerEmails';
import { LifetimePricingTools } from '@/components/admin/LifetimePricingTools';
import { LifetimeOverview } from '@/components/admin/LifetimeOverview';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png";

// Plan badge — single source of truth for how each subscription state renders in the user list.
// Keep keys aligned with the `plan_badge` field returned from /api/admin/users.
const PLAN_BADGE_STYLES = {
  lifetime: { label: 'Lifetime', cls: 'bg-[#CAAB84]/20 text-[#8B6F3F] border border-[#CAAB84]/40' },
  monthly_active: { label: 'Monthly · Active', cls: 'bg-[#87A6A7]/20 text-[#3F6062] border border-[#87A6A7]/40' },
  monthly_cancelling: { label: 'Monthly · Cancelling', cls: 'bg-[#CAAB84]/15 text-[#8B6F3F] border border-[#CAAB84]/30' },
  monthly_past_due: { label: 'Past Due', cls: 'bg-red-100 text-red-700 border border-red-200' },
  monthly_cancelled: { label: 'Cancelled', cls: 'bg-[#AA9283]/15 text-[#6B5D54] border border-[#AA9283]/30' },
  trial: { label: 'Trial', cls: 'bg-[#585C4E]/15 text-[#585C4E] border border-[#585C4E]/30' },
  expired: { label: 'Expired', cls: 'bg-[#AA9283]/10 text-[#AA9283] border border-[#AA9283]/20' },
  none: { label: 'No Access', cls: 'bg-gray-100 text-gray-500 border border-gray-200' },
};

function PlanBadge({ badge, fallbackPaid }) {
  // If the backend didn't compute a plan_badge yet (older API), fall back to the
  // legacy "Paid" indicator so the UI keeps working during rollout.
  const key = badge && PLAN_BADGE_STYLES[badge] ? badge : (fallbackPaid ? 'monthly_active' : 'none');
  const { label, cls } = PLAN_BADGE_STYLES[key];
  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cls}`}
      data-testid={`plan-badge-${key}`}
      title={`Plan: ${label}`}
    >
      {label}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, getAuthHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'payments', 'codes', 'invitations'
  const [paymentStats, setPaymentStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentSegment, setPaymentSegment] = useState(null); // null | 'full_access' | 'trial'
  const [invitationCodes, setInvitationCodes] = useState([]);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [sendingGiftEmail, setSendingGiftEmail] = useState(null); // Holds the email being processed
  const [bulkCount, setBulkCount] = useState(10);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  
  // Bulk invitation states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const checkAdminAndFetch = async () => {
      try {
        const checkRes = await axios.get(`${API}/admin/check`, { headers: getAuthHeader() });
        if (!checkRes.data.is_admin) {
          toast.error('Admin access required');
          navigate('/admin');
          return;
        }
        
        // Fetch stats and users
        const [statsRes, usersRes, paymentStatsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
          axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
          axios.get(`${API}/admin/payments/stats`, { headers: getAuthHeader() }),
        ]);
        
        setStats(statsRes.data);
        setUsers(usersRes.data.users);
        setTotalUsers(usersRes.data.total);
        setPaymentStats(paymentStatsRes.data);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        if (error.response?.status === 403) {
          toast.error('Admin access required');
          navigate('/admin');
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkAdminAndFetch();
  }, [getAuthHeader, navigate]);

  // Load payments when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'payments') {
      loadPayments();
    } else if (activeTab === 'codes') {
      loadInvitationCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, paymentSegment]);

  const loadPayments = async () => {
    try {
      const params = new URLSearchParams();
      if (paymentSearch) params.append('search', paymentSearch);
      if (paymentSegment) params.append('segment', paymentSegment);
      const qs = params.toString();
      const res = await axios.get(
        `${API}/admin/payments${qs ? `?${qs}` : ''}`,
        { headers: getAuthHeader() }
      );
      setPayments(res.data.payments);
    } catch (error) {
      toast.error('Failed to load payments');
    }
  };

  const loadInvitationCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/invitation-codes`, { headers: getAuthHeader() });
      setInvitationCodes(res.data.codes);
    } catch (error) {
      toast.error('Failed to load invitation codes');
    }
  };

  const generateNewCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await axios.post(`${API}/admin/invitation-codes/generate?count=1&code_type=admin_generated&access_type=full`, {}, { headers: getAuthHeader() });
      toast.success(`Generated full access code: ${res.data.codes[0]}`);
      loadInvitationCodes();
      // Refresh payment stats
      const statsRes = await axios.get(`${API}/admin/payments/stats`, { headers: getAuthHeader() });
      setPaymentStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const generateTrialCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await axios.post(`${API}/admin/invitation-codes/generate?count=1&code_type=trial&access_type=trial`, {}, { headers: getAuthHeader() });
      toast.success(`Generated trial code: ${res.data.codes[0]}`);
      loadInvitationCodes();
      // Refresh payment stats
      const statsRes = await axios.get(`${API}/admin/payments/stats`, { headers: getAuthHeader() });
      setPaymentStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to generate trial code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const generateBulkCodes = async () => {
    setGeneratingCode(true);
    try {
      const res = await axios.post(
        `${API}/admin/invitation-codes/generate?count=${bulkCount}&code_type=grandfathered`, 
        {}, 
        { headers: getAuthHeader() }
      );
      setGeneratedCodes(res.data.codes);
      toast.success(`Generated ${res.data.count} grandfathered codes!`);
      loadInvitationCodes();
      // Refresh payment stats
      const statsRes = await axios.get(`${API}/admin/payments/stats`, { headers: getAuthHeader() });
      setPaymentStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate codes');
    } finally {
      setGeneratingCode(false);
    }
  };

  const exportCodesToCSV = () => {
    const csvContent = "Invitation Code,Status,Type,Created\n" + 
      generatedCodes.map(code => `${code},Available,Grandfathered,${new Date().toLocaleDateString()}`).join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ambitiongpt_grandfathered_codes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Codes exported to CSV!');
  };

  const copyAllCodes = () => {
    const codesList = generatedCodes.join('\n');
    navigator.clipboard.writeText(codesList);
    toast.success(`${generatedCodes.length} codes copied to clipboard!`);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const deleteCode = async (code) => {
    if (!confirm(`Delete code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/invitation-codes/${code}`, { headers: getAuthHeader() });
      toast.success('Code deleted');
      loadInvitationCodes();
      // Refresh payment stats
      const statsRes = await axios.get(`${API}/admin/payments/stats`, { headers: getAuthHeader() });
      setPaymentStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete code');
    }
  };

  const handleSearch = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?search=${searchQuery}`, { headers: getAuthHeader() });
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const [grantingFullAccess, setGrantingFullAccess] = useState(false);

  const grantFullAccess = async () => {
    if (!userDetail?.user?.email) return;
    const email = userDetail.user.email;
    if (!window.confirm(
      `Grant FULL paid access to ${email}?\n\nThis will:\n` +
      `• Set accessType = "full"\n` +
      `• Set upgradeEligible = false\n` +
      `• Set paid = true\n\n` +
      `All chat history, journey progress, and onboarding data are preserved.\n\n` +
      `Use this when a customer paid but their access didn't update (e.g., PayPal checkout, expired session).`
    )) return;

    setGrantingFullAccess(true);
    try {
      const res = await axios.post(
        `${API}/admin/force-full-access/${encodeURIComponent(email)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`✅ ${email} now has full access`);
      // Refresh both the user detail panel and the user list
      await loadUserDetail(userDetail.user.id);
      const usersRes = await axios.get(
        `${API}/admin/users${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`,
        { headers: getAuthHeader() }
      );
      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total);
      console.log('Grant full access result:', res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant full access');
    } finally {
      setGrantingFullAccess(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/users`, { headers: getAuthHeader() });
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ambitiongpt_users_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Send the one-time "I owe you more time with Dea" gift email AND extend the
  // user's trial by 7 days. The backend rejects the call if the user already
  // upgraded to full access, so this only ever fires for un-converted trial users.
  const handleSendSevenDayGift = async (email) => {
    if (!email) {
      toast.error('No customer email on this payment');
      return;
    }
    if (!window.confirm(
      `Send the "I owe you more time with Dea" gift email to ${email}?\n\n` +
      `This will:\n` +
      `  • Extend their trial by 7 days (restart from today if expired)\n` +
      `  • Re-schedule the 3-email trial sequence\n` +
      `  • Send the apology + reactivation email\n\n` +
      `If they already upgraded to full access, the action will be rejected automatically.`
    )) return;

    setSendingGiftEmail(email);
    try {
      const res = await axios.post(
        `${API}/admin/extend-trial/${encodeURIComponent(email)}`,
        { days: 7, send_gift_email: true, restart_email_sequence: true },
        { headers: getAuthHeader() }
      );
      toast.success(`🎁 7-day gift sent to ${email} (${res.data.mode})`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send gift';
      toast.error(detail);
    } finally {
      setSendingGiftEmail(null);
    }
  };

  const getPhaseColor = (phase) => {
    const colors = {
      alignment: 'bg-[#87A6A7]/20 text-[#87A6A7]',
      amplification: 'bg-[#CAAB84]/20 text-[#CAAB84]',
      actualization: 'bg-[#AA9283]/20 text-[#AA9283]',
      not_started: 'bg-[#EAD8C2] text-[#B1897C]'
    };
    return colors[phase] || colors.not_started;
  };

  // Download sample CSV template for bulk invitations
  const downloadSampleTemplate = () => {
    const csvContent = "email,name\njane@example.com,Jane Smith\nmary@example.com,Mary Johnson\nsarah@example.com,Sarah Williams\n";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ambitiongpt_invitation_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Sample template downloaded!');
  };

  // Send single invitation email
  const handleSingleInvite = async () => {
    if (!inviteEmail) return;
    
    setSendingInvite(true);
    try {
      const res = await axios.post(
        `${API}/admin/invitations/send-single`,
        { email: inviteEmail, name: inviteName || null },
        { headers: getAuthHeader() }
      );
      toast.success(`Invitation sent to ${inviteEmail}!`);
      setInviteEmail('');
      setInviteName('');
      loadInvitationCodes(); // Refresh codes list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  // Handle bulk file upload and send invitations
  const handleBulkFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setUploadingFile(true);
    setBulkResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(
        `${API}/admin/invitations/send-bulk`,
        formData,
        { 
          headers: { 
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setBulkResults(res.data);
      
      if (res.data.sent > 0) {
        toast.success(`Successfully sent ${res.data.sent} invitation(s)!`);
      }
      if (res.data.skipped > 0) {
        toast.info(`${res.data.skipped} email(s) skipped (already have accounts)`);
      }
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} email(s) failed to send`);
      }

      loadInvitationCodes(); // Refresh codes list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process file');
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCF7] flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Admin Dashboard"
        description="Dea Admin Dashboard - User management, analytics, and invitation codes."
        noindex={true}
      />
      <div className="min-h-screen bg-[#FFFCF7]">
      {/* Header */}
      <header className="bg-[#FFFCF7] border-b border-[#EAD8C2] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Dea" className="h-10 w-auto object-contain" />
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#87A6A7]/10">
              <Shield className="h-4 w-4 text-[#87A6A7]" />
              <span className="text-sm font-medium text-[#87A6A7]">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#AA9283] text-sm">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate('/admin'); }}
              className="text-[#B1897C] hover:text-[#B1897C]"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl text-[#726158]">Dashboard</h1>
            <p className="text-[#AA9283]">Overview of the Dea platform</p>
          </div>
          <Button onClick={handleExport} className="btn-outline-gold">
            <Download className="h-4 w-4 mr-2" />
            Export Users
          </Button>
        </div>

        <LifetimeOverview />

        {stats && paymentStats && (
          <div className="grid sm:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-2xl p-6"
              data-testid="total-users-card"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#585C4E]/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#585C4E]" />
                </div>
                <span className="text-[#AA9283] text-sm">Total Users</span>
              </div>
              <p className="font-serif text-3xl text-[#726158]">{stats.total_users}</p>
              <p className="text-[#B1897C] text-xs mt-1">{stats.active_users_7d} active this week</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="glass-card rounded-2xl p-6"
              data-testid="total-messages-card"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#B1897C]/10 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-[#B1897C]" />
                </div>
                <span className="text-[#AA9283] text-sm">Total Messages</span>
              </div>
              <p className="font-serif text-3xl text-[#726158]">{stats.total_messages.toLocaleString()}</p>
              <p className="text-[#B1897C] text-xs mt-1">Conversations with Dea</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-2xl p-6"
              data-testid="invitation-codes-card"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#AA9283]/10 flex items-center justify-center">
                  <Ticket className="h-5 w-5 text-[#AA9283]" />
                </div>
                <span className="text-[#AA9283] text-sm">Invitation Codes</span>
              </div>
              <p className="font-serif text-3xl text-[#726158]">{paymentStats.invitation_codes?.unused || 0}</p>
              <p className="text-[#B1897C] text-xs mt-1">{paymentStats.invitation_codes?.used || 0} used of {paymentStats.invitation_codes?.total || 0}</p>
            </motion.div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-[#EAD8C2]">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'users' 
                ? 'text-[#87A6A7] border-b-2 border-[#87A6A7]' 
                : 'text-[#AA9283] hover:text-[#726158]'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'payments' 
                ? 'text-[#87A6A7] border-b-2 border-[#87A6A7]' 
                : 'text-[#AA9283] hover:text-[#726158]'
            }`}
          >
            <CreditCard className="h-4 w-4 inline mr-2" />
            Payments
          </button>
          <button
            onClick={() => setActiveTab('codes')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'codes' 
                ? 'text-[#87A6A7] border-b-2 border-[#87A6A7]' 
                : 'text-[#AA9283] hover:text-[#726158]'
            }`}
          >
            <Ticket className="h-4 w-4 inline mr-2" />
            Invitation Codes
          </button>
          <button
            onClick={() => setActiveTab('becomingher')}
            data-testid="admin-tab-becomingher"
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'becomingher' 
                ? 'text-[#87A6A7] border-b-2 border-[#87A6A7]' 
                : 'text-[#AA9283] hover:text-[#726158]'
            }`}
          >
            <Mail className="h-4 w-4 inline mr-2" />
            Becoming Her Emails
          </button>
          <button
            onClick={() => setActiveTab('lifetime')}
            data-testid="admin-tab-lifetime"
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'lifetime' 
                ? 'text-[#87A6A7] border-b-2 border-[#87A6A7]' 
                : 'text-[#AA9283] hover:text-[#726158]'
            }`}
          >
            <Crown className="h-4 w-4 inline mr-2" />
            Lifetime & Pricing
          </button>
        </div>

        {activeTab === 'lifetime' && <LifetimePricingTools />}
        {activeTab === 'becomingher' && <BecomingHerEmails />}

        {/* Tab Content */}
        {activeTab === 'users' && (
          <>
            {/* Phase Distribution */}
            {stats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-card rounded-2xl p-6 mb-6"
              >
                <h2 className="font-serif text-xl text-[#726158] mb-4">User Phase Distribution</h2>
                <div className="grid sm:grid-cols-4 gap-4">
                  {Object.entries(stats.phase_distribution).map(([phase, count]) => (
                    <div key={phase} className={`rounded-xl p-4 ${getPhaseColor(phase)}`}>
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-sm capitalize">{phase.replace('_', ' ')}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Users Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <h2 className="font-serif text-xl text-[#726158]">Users ({totalUsers})</h2>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B1897C]" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="input-light pl-10 w-full sm:w-64"
                    />
                  </div>
                  <Button onClick={handleSearch} variant="outline" size="sm">Search</Button>
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-6">
                {/* Users List */}
                <div className="lg:col-span-2">
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-2 pr-4">
                      {users.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => loadUserDetail(u.id)}
                          className={`p-4 rounded-xl cursor-pointer transition-colors ${
                            selectedUser === u.id 
                              ? 'bg-[#87A6A7]/10 border border-[#87A6A7]/30' 
                              : 'bg-[#EAD8C2]/30 hover:bg-[#EAD8C2]/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-[#726158]">{u.name}</p>
                              <p className="text-sm text-[#B1897C]">{u.email}</p>
                            </div>
                            <div className="text-right flex flex-col items-end gap-1.5">
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getPhaseColor(u.current_phase || 'not_started')}`}>
                                <Sparkles className="h-3 w-3" />
                                Day {u.current_day || 0}
                              </div>
                              <PlanBadge badge={u.plan_badge} fallbackPaid={u.paid} />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-[#B1897C]">
                            <span>{u.total_messages} messages</span>
                            <span>{u.total_wins} wins</span>
                            {u.last_active && (
                              <span>Last active: {new Date(u.last_active).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {/* User Detail Panel */}
                <div className="bg-[#EAD8C2]/30 rounded-xl p-6">
                  {userDetail ? (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-serif text-xl text-[#726158]">{userDetail.user.name}</h3>
                        <p className="text-sm text-[#B1897C]">{userDetail.user.email}</p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-[#AA9283]">Joined</span>
                          <span className="text-[#726158]">{new Date(userDetail.user.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#AA9283]">Payment Status</span>
                          <span className={userDetail.user.paid ? 'text-[#87A6A7]' : 'text-[#B1897C]'}>
                            {userDetail.user.paid ? 'Paid' : 'Free'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#AA9283]">Journey Day</span>
                          <span className="text-[#726158]">{userDetail.user.current_day || 0} / 90</span>
                        </div>
                        <Progress value={(userDetail.user.current_day || 0) / 90 * 100} className="h-2" />
                        <div className="flex justify-between">
                          <span className="text-[#AA9283]">Phase</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getPhaseColor(userDetail.user.current_phase || 'not_started')}`}>
                            {(userDetail.user.current_phase || 'Not Started').replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#AA9283]">Cycle Sync</span>
                          <span className="text-[#726158]">{userDetail.user.cycle_sync_enabled ? 'Enabled' : 'Disabled'}</span>
                        </div>
                      </div>

                      {/* Admin Actions */}
                      <div className="pt-4 border-t border-[#EAD8C2]">
                        {(() => {
                          const u = userDetail.user;
                          const alreadyFull = u.accessType === 'full' && u.paid === true && u.upgradeEligible === false;
                          return (
                            <Button
                              data-testid="grant-full-access-btn"
                              onClick={grantFullAccess}
                              disabled={grantingFullAccess}
                              className={alreadyFull
                                ? 'w-full bg-[#EAD8C2]/40 hover:bg-[#EAD8C2]/60 text-[#726158] border border-[#EAD8C2]'
                                : 'w-full bg-[#87A6A7] hover:bg-[#87A6A7]/90 text-white'}
                            >
                              {grantingFullAccess ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Granting...
                                </>
                              ) : alreadyFull ? (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Already Full Access — Re-apply
                                </>
                              ) : (
                                <>
                                  <Shield className="h-4 w-4 mr-2" />
                                  Grant Full Access
                                </>
                              )}
                            </Button>
                          );
                        })()}
                        <p className="text-xs text-[#B1897C] mt-2 leading-relaxed">
                          Use when a customer paid but their access didn't update (e.g., PayPal checkout, expired Stripe session). All data is preserved.
                        </p>
                      </div>

                      <div className="pt-4 border-t border-[#EAD8C2]">
                        <h4 className="font-medium text-[#726158] mb-3">Activity Stats</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-[#FFFCF7] rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-[#CAAB84]">{userDetail.stats.total_messages}</p>
                            <p className="text-xs text-[#B1897C]">Messages</p>
                          </div>
                          <div className="bg-[#FFFCF7] rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-[#87A6A7]">{userDetail.stats.total_wins}</p>
                            <p className="text-xs text-[#B1897C]">Wins</p>
                          </div>
                          <div className="bg-[#FFFCF7] rounded-lg p-3 text-center col-span-2">
                            <p className="text-2xl font-bold text-[#AA9283]">{userDetail.stats.total_sessions}</p>
                            <p className="text-xs text-[#B1897C]">Chat Sessions</p>
                          </div>
                        </div>
                      </div>

                      {userDetail.journey?.main_goal && (
                        <div className="pt-4 border-t border-[#EAD8C2]">
                          <h4 className="font-medium text-[#726158] mb-2">Main Goal</h4>
                          <p className="text-sm text-[#AA9283] bg-[#FFFCF7] rounded-lg p-3">
                            {userDetail.journey.main_goal}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-[#B1897C]">
                      <div className="text-center">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>Select a user to view details</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-serif text-xl text-[#726158]">Payment History</h2>
              <Button
                data-testid="reconcile-payments-btn"
                onClick={async () => {
                  try {
                    toast.info('Checking all pending transactions with Stripe...');
                    const res = await axios.post(`${API}/admin/reconcile-payments`, {}, { headers: getAuthHeader() });
                    const r = res.data.results;
                    toast.success(`Reconciled ${res.data.total_checked} transactions: ${r.updated_to_paid} confirmed paid, ${r.marked_abandoned + r.marked_expired} abandoned`);
                    // Refresh payments list (respect current filters)
                    await loadPayments();
                    const statsRes = await axios.get(`${API}/admin/payments/stats`, { headers: getAuthHeader() });
                    setPaymentStats(statsRes.data);
                  } catch (err) {
                    toast.error('Failed to reconcile payments');
                  }
                }}
                className="bg-[#87A6A7] hover:bg-[#87A6A7]/80 text-white text-sm px-4 py-2"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reconcile with Stripe
              </Button>
            </div>

            {/* Search + active filter row */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                <div className="relative flex-1">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B1897C]" />
                  <Input
                    data-testid="payment-search-input"
                    type="text"
                    placeholder="Search by email, name, or invitation code..."
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadPayments(); }}
                    className="pl-9 bg-[#FFFCF7] border-[#EAD8C2]"
                  />
                </div>
                <Button
                  data-testid="payment-search-btn"
                  onClick={loadPayments}
                  variant="outline"
                  className="border-[#EAD8C2] text-[#726158]"
                >
                  Search
                </Button>
                {paymentSearch && (
                  <Button
                    data-testid="payment-search-clear-btn"
                    onClick={() => { setPaymentSearch(''); setTimeout(loadPayments, 0); }}
                    variant="ghost"
                    className="text-[#B1897C] hover:text-[#726158]"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {paymentSegment && (
                <div
                  data-testid="payment-segment-pill"
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                    paymentSegment === 'full_access'
                      ? 'bg-[#87A6A7]/15 text-[#87A6A7]'
                      : 'bg-[#CAAB84]/15 text-[#CAAB84]'
                  }`}
                >
                  {paymentSegment === 'full_access' ? (
                    <>
                      <DollarSign className="h-3.5 w-3.5" />
                      Filter: Full Access ($88 / $97)
                    </>
                  ) : (
                    <>
                      <Clock className="h-3.5 w-3.5" />
                      Filter: Trial Purchases ($9)
                    </>
                  )}
                  <button
                    data-testid="payment-segment-clear"
                    onClick={() => setPaymentSegment(null)}
                    className="ml-1 hover:opacity-70"
                    aria-label="Clear filter"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <span className="text-xs text-[#B1897C] ml-auto" data-testid="payment-results-count">
                {payments.length} result{payments.length === 1 ? '' : 's'}
              </span>
            </div>
            
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {payments.length === 0 ? (
                  <div className="text-center py-12 text-[#B1897C]">
                    <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No payments yet</p>
                  </div>
                ) : (
                  payments.map((payment, idx) => (
                    <div
                      key={payment.session_id || idx}
                      className="p-4 rounded-xl bg-[#EAD8C2]/30 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          payment.payment_status === 'paid' 
                            ? 'bg-[#87A6A7]/20' 
                            : payment.payment_status === 'abandoned'
                            ? 'bg-[#B1897C]/10'
                            : payment.payment_status === 'pending'
                            ? 'bg-[#CAAB84]/20'
                            : 'bg-[#B1897C]/20'
                        }`}>
                          {payment.payment_status === 'paid' ? (
                            <CheckCircle className="h-5 w-5 text-[#87A6A7]" />
                          ) : payment.payment_status === 'abandoned' ? (
                            <XCircle className="h-5 w-5 text-[#B1897C]/50" />
                          ) : payment.payment_status === 'pending' ? (
                            <Clock className="h-5 w-5 text-[#CAAB84]" />
                          ) : (
                            <XCircle className="h-5 w-5 text-[#B1897C]" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#726158]">{payment.product_name}</p>
                          <p className="text-sm text-[#B1897C]">
                            {new Date(payment.created_at).toLocaleDateString()} at {new Date(payment.created_at).toLocaleTimeString()}
                          </p>
                          {payment.customer_email && (
                            <p className="text-xs text-[#AA9283] mt-0.5">{payment.customer_email}</p>
                          )}
                          {payment.invitation_code && (
                            <p className="text-xs text-[#87A6A7] font-mono mt-1">
                              Code: {payment.invitation_code}
                            </p>
                          )}
                          {payment.abandoned_email_sent && (
                            <p className="text-xs text-[#CAAB84] mt-1">Recovery email sent</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <div>
                          <p className="font-serif text-xl text-[#726158]">${payment.amount}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            payment.payment_status === 'paid' 
                              ? 'bg-[#87A6A7]/20 text-[#87A6A7]' 
                              : payment.payment_status === 'abandoned'
                              ? 'bg-[#B1897C]/10 text-[#B1897C]/60'
                              : payment.payment_status === 'pending'
                              ? 'bg-[#CAAB84]/20 text-[#CAAB84]'
                              : 'bg-[#B1897C]/20 text-[#B1897C]'
                          }`}>
                            {payment.payment_status}
                          </span>
                        </div>
                        {/* Send 7-Day Gift — only for paid TRIAL purchases who didn't convert.
                            Backend rejects if user already upgraded to full access.
                            Matches both new (access_type field) and legacy (product_name contains "Power Start") records. */}
                        {payment.payment_status === 'paid' && payment.customer_email && (
                          payment.access_type === 'trial' ||
                          (payment.product_name || '').toLowerCase().includes('power start')
                        ) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-[#B8956A] text-[#B8956A] hover:bg-[#B8956A]/10 whitespace-nowrap"
                            disabled={sendingGiftEmail === payment.customer_email}
                            onClick={() => handleSendSevenDayGift(payment.customer_email)}
                            data-testid={`send-gift-btn-${payment.session_id || payment.customer_email}`}
                          >
                            {sendingGiftEmail === payment.customer_email ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending…</>
                            ) : (
                              <><Gift className="h-3 w-3 mr-1" /> Send 7-Day Gift</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </motion.div>
        )}

        {/* Invitation Codes Tab */}
        {activeTab === 'codes' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Bulk Email Invitation Card */}
            <div className="glass-card rounded-2xl p-6 border-2 border-[#87A6A7]/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#87A6A7]/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-[#87A6A7]" />
                </div>
                <div>
                  <h2 className="font-serif text-xl text-[#726158]">Bulk Email Invitations</h2>
                  <p className="text-[#AA9283] text-sm">Send branded invitation emails to multiple clients at once</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Single Invite Form */}
                <div className="bg-[#EAD8C2]/20 rounded-xl p-4">
                  <h3 className="font-medium text-[#726158] mb-3 flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Send Single Invitation
                  </h3>
                  <div className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="input-light"
                      data-testid="single-invite-email"
                    />
                    <Input
                      type="text"
                      placeholder="Name (optional)"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="input-light"
                      data-testid="single-invite-name"
                    />
                    <Button
                      onClick={handleSingleInvite}
                      disabled={sendingInvite || !inviteEmail}
                      className="btn-gold w-full"
                      data-testid="send-single-invite-btn"
                    >
                      {sendingInvite ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Invitation Email
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Bulk Upload Form */}
                <div className="bg-[#87A6A7]/10 rounded-xl p-4">
                  <h3 className="font-medium text-[#726158] mb-3 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    Bulk Upload (CSV/Excel)
                  </h3>
                  <div className="space-y-3">
                    <p className="text-sm text-[#AA9283]">
                      Upload a file with <strong>email</strong> and <strong>name</strong> columns.
                    </p>
                    <Button
                      onClick={downloadSampleTemplate}
                      variant="outline"
                      size="sm"
                      className="w-full border-[#87A6A7] text-[#87A6A7]"
                      data-testid="download-template-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Sample Template
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".csv,.xlsx,.xls"
                      onChange={handleBulkFileUpload}
                      className="hidden"
                      data-testid="bulk-file-input"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      className="btn-gold w-full"
                      data-testid="upload-bulk-btn"
                    >
                      {uploadingFile ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload & Send Invitations
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Bulk Results Display */}
              {bulkResults && (
                <div className="mt-6 p-4 rounded-xl border border-[#87A6A7]/30 bg-[#FFFCF7]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-[#726158]">Bulk Invitation Results</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBulkResults(null)}
                      className="text-[#B1897C]"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 rounded-lg bg-[#87A6A7]/10">
                      <p className="text-2xl font-bold text-[#87A6A7]">{bulkResults.sent}</p>
                      <p className="text-xs text-[#87A6A7]">Sent</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-[#CAAB84]/10">
                      <p className="text-2xl font-bold text-[#CAAB84]">{bulkResults.skipped}</p>
                      <p className="text-xs text-[#CAAB84]">Skipped</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-[#B1897C]/10">
                      <p className="text-2xl font-bold text-[#B1897C]">{bulkResults.failed}</p>
                      <p className="text-xs text-[#B1897C]">Failed</p>
                    </div>
                  </div>
                  {bulkResults.details && bulkResults.details.length > 0 && (
                    <ScrollArea className="h-48">
                      <div className="space-y-2">
                        {bulkResults.details.map((detail, idx) => (
                          <div
                            key={idx}
                            className={`text-sm p-2 rounded-lg flex items-center justify-between ${
                              detail.status === 'sent' 
                                ? 'bg-[#87A6A7]/10 text-[#87A6A7]' 
                                : detail.status === 'skipped'
                                ? 'bg-[#CAAB84]/10 text-[#CAAB84]'
                                : 'bg-[#B1897C]/10 text-[#B1897C]'
                            }`}
                          >
                            <span className="font-mono">{detail.email}</span>
                            <span className="text-xs">
                              {detail.status === 'sent' && '✓ Sent'}
                              {detail.status === 'skipped' && `⊘ ${detail.reason}`}
                              {detail.status === 'failed' && `✗ ${detail.reason}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>

            {/* Bulk Generation Card */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="font-serif text-xl text-[#726158] mb-4">Generate Grandfathered Codes</h2>
              <p className="text-[#AA9283] text-sm mb-4">
                Generate bulk codes for existing customers from your previous ChatGPT version. 
                Each code is single-use (one code per user).
              </p>
              
              <div className="flex items-end gap-4 mb-4">
                <div>
                  <label className="text-sm text-[#AA9283] block mb-2">Number of Codes</label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={bulkCount}
                    onChange={(e) => setBulkCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="input-light w-32"
                  />
                </div>
                <Button
                  onClick={generateBulkCodes}
                  disabled={generatingCode}
                  className="btn-gold"
                >
                  {generatingCode ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Generate {bulkCount} Codes
                </Button>
              </div>

              {/* Generated Codes Display */}
              {generatedCodes.length > 0 && (
                <div className="mt-6 p-4 bg-[#87A6A7]/10 rounded-xl border border-[#87A6A7]/20">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-[#726158]">
                      Generated {generatedCodes.length} Grandfathered Codes
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={copyAllCodes}
                        variant="outline"
                        size="sm"
                        className="border-[#87A6A7] text-[#87A6A7]"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy All
                      </Button>
                      <Button
                        onClick={exportCodesToCSV}
                        variant="outline"
                        size="sm"
                        className="border-[#CAAB84] text-[#CAAB84]"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto bg-[#FFFCF7] rounded-lg p-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {generatedCodes.map((code, idx) => (
                        <div
                          key={code}
                          className="font-mono text-sm text-[#726158] bg-[#EAD8C2]/30 px-2 py-1 rounded cursor-pointer hover:bg-[#EAD8C2]/50"
                          onClick={() => { navigator.clipboard.writeText(code); toast.success('Code copied!'); }}
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-[#B1897C] mt-2">
                    Click any code to copy it. These codes are now saved and visible in the list below.
                  </p>
                </div>
              )}
            </div>

            {/* Existing Codes List */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-[#726158]">All Invitation Codes</h2>
                <div className="flex gap-2">
                  <Button
                    onClick={generateTrialCode}
                    disabled={generatingCode}
                    variant="outline"
                    className="border-[#AA9283] text-[#AA9283] hover:bg-[#AA9283]/10"
                    data-testid="generate-trial-code-btn"
                  >
                    {generatingCode ? (
                      <span className="spinner mr-2" />
                    ) : (
                      <Clock className="h-4 w-4 mr-2" />
                    )}
                    Trial Code
                  </Button>
                  <Button
                    onClick={generateNewCode}
                    disabled={generatingCode}
                    variant="outline"
                    className="border-[#CAAB84] text-[#CAAB84] hover:bg-[#CAAB84]/10"
                    data-testid="generate-full-code-btn"
                  >
                    {generatingCode ? (
                      <span className="spinner mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Full Access Code
                  </Button>
                </div>
              </div>
              
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {invitationCodes.length === 0 ? (
                    <div className="text-center py-12 text-[#B1897C]">
                      <Ticket className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No invitation codes yet</p>
                      <p className="text-sm mt-2">Generate codes above to get started</p>
                    </div>
                  ) : (
                    invitationCodes.map((code) => (
                      <div
                        key={code.code}
                        className={`p-4 rounded-xl flex items-center justify-between ${
                          code.used ? 'bg-[#EAD8C2]/20' : 'bg-[#87A6A7]/10 border border-[#87A6A7]/20'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            code.used ? 'bg-[#B1897C]/20' : 'bg-[#87A6A7]/20'
                          }`}>
                            {code.used ? (
                              <CheckCircle className="h-5 w-5 text-[#B1897C]" />
                            ) : (
                              <Ticket className="h-5 w-5 text-[#87A6A7]" />
                            )}
                          </div>
                          <div>
                            <p className="font-mono text-lg text-[#726158] tracking-wider">{code.code}</p>
                            <p className="text-sm text-[#B1897C]">
                              Created: {new Date(code.created_at).toLocaleDateString()}
                              {code.access_type === 'trial' && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#AA9283]/20 text-[#AA9283]">
                                  Trial ($9)
                                </span>
                              )}
                              {code.type === 'grandfathered' && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#CAAB84]/20 text-[#CAAB84]">
                                  Grandfathered
                                </span>
                              )}
                              {code.type === 'admin_generated' && !code.access_type && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#87A6A7]/20 text-[#87A6A7]">
                                  Full Access
                                </span>
                              )}
                              {code.access_type === 'full' && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#87A6A7]/20 text-[#87A6A7]">
                                  Full Access
                                </span>
                              )}
                            </p>
                            {code.used && code.used_at && (
                              <p className="text-xs text-[#B1897C]">
                                Used: {new Date(code.used_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            code.used 
                              ? 'bg-[#B1897C]/20 text-[#B1897C]' 
                              : 'bg-[#87A6A7]/20 text-[#87A6A7]'
                          }`}>
                            {code.used ? 'Used' : 'Available'}
                          </span>
                          {!code.used && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyCode(code.code)}
                                className="text-[#AA9283] hover:text-[#87A6A7]"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteCode(code.code)}
                                className="text-[#B1897C] hover:text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </motion.div>
        )}
      </main>
    </div>
    </>
  );
}
